/**
 * Phase 4 slice 3 — Cube Test detail. View pour + cube set, post 7/14/28-day
 * results per cube, mark reviewed (approve-gated). Shows IS-456 acceptance and
 * any auto-raised linked NCR.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FlaskConical, CheckCircle2, XCircle, Loader2, Plus, FileDown } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import AuthImage from '../../components/safety/AuthImage';
import { apiGet, apiPut, apiPost, apiDownload } from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';

const AGES = ['7d', '14d', '28d'];
import { formatDate as fmt } from '../../lib/format';
const resTone = (r) => (r === 'pass' ? 'text-emerald-600' : r === 'fail' ? 'text-red-600' : 'text-slate-400');

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">{label}</div>
    <div className="text-sm text-slate-800">{value || '—'}</div>
  </div>
);

export default function CubeTestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [entry, setEntry] = useState({ cube_id: '', age: '28d', load_kn: '', area_mm2: '22500', strength_n_mm2: '', failure_mode: '' });

  const load = () => apiGet(`/quality/cube-tests/${id}`).then(setDoc).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const openEntry = (cube_id, age) => {
    setEntry({ cube_id, age, load_kn: '', area_mm2: '22500', strength_n_mm2: '', failure_mode: '' });
    setOpen(true);
  };

  const submitResult = async () => {
    setBusy(true);
    try {
      await apiPut(`/quality/cube-tests/${id}/result/${entry.cube_id}/${entry.age}`, {
        load_kn: entry.load_kn ? Number(entry.load_kn) : null,
        area_mm2: entry.area_mm2 ? Number(entry.area_mm2) : null,
        strength_n_mm2: entry.strength_n_mm2 ? Number(entry.strength_n_mm2) : null,
        failure_mode: entry.failure_mode,
      });
      toast.success('Result recorded');
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'No permission to record results' : (e?.response?.data?.detail || 'Failed'));
    } finally { setBusy(false); }
  };

  const review = async (result) => {
    setBusy(true);
    try {
      await apiPost(`/quality/cube-tests/${id}/review`, { result });
      toast.success(result === 'approved' ? 'Cube test approved' : 'Marked non-conforming');
      load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'You do not have permission to review.' : 'Failed');
    } finally { setBusy(false); }
  };

  if (!doc) return <div className="text-center text-xs text-slate-400 py-16 uppercase tracking-widest font-display">Loading…</div>;
  const gradeLabel = doc.concrete_grade === 'Other' ? (doc.concrete_grade_other || 'Other') : doc.concrete_grade;
  const met = doc.acceptance_criteria_met;

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={() => navigate('/app/quality/cube-tests')} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-3" data-testid="ct-back"><ArrowLeft size={14} /> Back to Cube Tests</button>
      <PageHeader overline={`Cube Test · ${gradeLabel}`} title={doc.pour_location_on_site || 'Concrete Pour'}
        actions={(
          <div className="flex items-center gap-2">
            <button onClick={() => apiDownload(`/quality/cube-tests/${id}/certificate.pdf`, `CubeCert_${id.slice(0, 8)}.pdf`).catch(() => toast.error('PDF failed'))}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50" data-testid="ct-pdf-btn">
              <FileDown size={13} /> Certificate
            </button>
            {met === true ? <span className="tag border bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="ct-acceptance">Acceptance: Pass</span>
              : met === false ? <span className="tag border bg-red-50 text-red-700 border-red-200" data-testid="ct-acceptance">Acceptance: Fail</span>
                : <span className="tag border bg-slate-100 text-slate-500 border-slate-200" data-testid="ct-acceptance">Acceptance: Pending 28d</span>}
          </div>
        )}
      />

      {met === false && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-4 text-sm text-red-700 flex items-center justify-between" data-testid="ct-fail-banner">
          <span>This pour failed IS-456 28-day acceptance.{doc.linked_ncr_id && ' An NCR was auto-raised.'}</span>
          {doc.linked_ncr_id && <Link to={`/app/quality/ncrs/${doc.linked_ncr_id}`} className="font-display font-bold underline" data-testid="ct-linked-ncr">View NCR →</Link>}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5" data-testid="cube-test-detail">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Field label="Grade" value={gradeLabel} />
          <Field label="Target (N/mm²)" value={doc.target_strength_n_mm2 != null ? `${doc.target_strength_n_mm2}` : '—'} />
          <Field label="Pour date" value={fmt(doc.pour_date)} />
          <Field label="Mix ref" value={doc.mix_design_ref} />
          <Field label="Batch / docket" value={doc.batch_number} />
          <Field label="Supplier" value={doc.supplier_name ? `${doc.supplier} · ${doc.supplier_name}` : doc.supplier} />
          <Field label="Slump (mm)" value={doc.slump_mm != null ? `${doc.slump_mm}` : '—'} />
          <Field label="Status" value={(doc.status || '').replace('_', ' ')} />
        </div>

        {/* Cube set + results matrix */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Cube set — strengths (N/mm²)</div>
          <div className="overflow-x-auto border border-slate-200 rounded-sm">
            <table className="w-full text-sm" data-testid="ct-result-matrix">
              <thead><tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <th className="text-left p-2">Cube</th><th className="text-left p-2">Cast</th>
                {AGES.map((a) => <th key={a} className="p-2 text-center">{a}</th>)}
              </tr></thead>
              <tbody>
                {(doc.cube_set || []).map((c) => (
                  <tr key={c.cube_id} className="border-t border-slate-100">
                    <td className="p-2 font-display font-bold">{c.cube_id}</td>
                    <td className="p-2 text-xs text-slate-500">{fmt(c.cast_date)}</td>
                    {AGES.map((a) => {
                      const r = (c.test_results || {})[a] || {};
                      return (
                        <td key={a} className="p-2 text-center">
                          {r.strength_n_mm2 != null ? (
                            <span className={`font-display font-bold ${resTone(r.result)}`}>{r.strength_n_mm2}</span>
                          ) : (
                            <button onClick={() => openEntry(c.cube_id, a)} className="inline-flex items-center gap-1 text-[11px] text-[#3b4872] hover:underline" data-testid={`ct-enter-${c.cube_id}-${a}`}><Plus size={11} /> Enter</button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-400 mt-1">Acceptance (IS-456, simplified): each cube ≥ 85% of target at 28d, group average ≥ target+4 (M20+).</p>
        </div>

        {doc.notes && <Field label="Notes" value={doc.notes} />}

        {(doc.photos || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Photos ({doc.photos.length})</div>
            <div className="flex flex-wrap gap-2">
              {doc.photos.map((p) => (
                <AuthImage key={p.id} src={p.image_url} alt="pour" className="w-24 h-24 object-cover rounded-sm border border-slate-200" />
              ))}
            </div>
          </div>
        )}

        {/* Review actions */}
        <div className="border-t border-slate-200 pt-4 flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mr-2">Review</div>
          <button onClick={() => review('approved')} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-display font-bold rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="ct-approve-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Approve
          </button>
          <button onClick={() => review('non_conforming')} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-display font-bold rounded-sm border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60" data-testid="ct-reject-btn">
            <XCircle size={14} /> Non-Conforming
          </button>
          {doc.reviewed_by && <span className="text-xs text-slate-500 ml-2">Reviewed by {doc.reviewed_by} · {fmt(doc.reviewed_at)}</span>}
        </div>
      </div>

      {/* Result entry dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><span /></DialogTrigger>
        <DialogContent data-testid="ct-result-dialog">
          <DialogHeader><DialogTitle><FlaskConical size={16} className="inline mr-1.5" />Cube {entry.cube_id} · {entry.age} result</DialogTitle>
            <DialogDescription>Enter the compression load + cube area, or the strength directly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Load (kN)</div>
                <input type="number" value={entry.load_kn} onChange={(e) => setEntry({ ...entry, load_kn: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="ct-res-load" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Area (mm²)</div>
                <input type="number" value={entry.area_mm2} onChange={(e) => setEntry({ ...entry, area_mm2: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="ct-res-area" />
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">…or strength directly (N/mm²)</div>
              <input type="number" value={entry.strength_n_mm2} onChange={(e) => setEntry({ ...entry, strength_n_mm2: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="ct-res-strength" />
            </div>
            <input value={entry.failure_mode} onChange={(e) => setEntry({ ...entry, failure_mode: e.target.value })} placeholder="Failure mode (optional)" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="ct-res-failure" />
          </div>
          <DialogFooter>
            <button onClick={submitResult} disabled={busy} className="px-4 py-2 text-sm font-display font-bold rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e] disabled:opacity-60" data-testid="ct-res-submit">{busy ? 'Saving…' : 'Save Result'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
